import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo, useRef, useEffect, useState } from "react";
import { SIcon } from "./components";
export const SMultiSelect = ({ options, values, onChange, className, tabIndex, placeholder = "Select options...", lineClamp, allowDuplicates = false })=>{
    const containerRef = useRef(null);
    const [visiblePills, setVisiblePills] = useState(values);
    const [showMore, setShowMore] = useState(false);
    const getLabel = (optionValue)=>{
        const option = options.find((opt)=>String(opt.value) === String(optionValue));
        return option ? option.label : String(optionValue);
    };
    const availableOptions = useMemo(()=>{
        return allowDuplicates ? options : options.filter((option)=>!values.includes(option.value));
    }, [
        options,
        values,
        allowDuplicates
    ]);
    useEffect(()=>{
        if (containerRef.current) {
            const container = containerRef.current;
            const containerWidth = container.offsetWidth - 40 // subtract padding and chevron
            ;
            let totalWidth = 0;
            let visibleCount = 0;
            let rowCount = 0;
            const tempDiv = document.createElement("div");
            tempDiv.style.visibility = "hidden";
            tempDiv.style.position = "absolute";
            tempDiv.style.whiteSpace = "nowrap";
            tempDiv.style.maxWidth = "120px";
            tempDiv.style.overflow = "hidden";
            tempDiv.style.textOverflow = "ellipsis";
            document.body.appendChild(tempDiv);
            for(let i = 0; i < values.length; i++){
                tempDiv.textContent = getLabel(values[i]);
                const pillWidth = tempDiv.offsetWidth + 30 // additional padding for the remove button
                ;
                if (totalWidth + pillWidth > containerWidth) {
                    rowCount++;
                    if (lineClamp && rowCount >= lineClamp) {
                        break;
                    }
                    totalWidth = pillWidth;
                } else {
                    totalWidth += pillWidth;
                }
                visibleCount++;
            }
            document.body.removeChild(tempDiv);
            setVisiblePills(values.slice(0, visibleCount));
            setShowMore(visibleCount < values.length);
        }
    }, [
        values,
        containerRef,
        lineClamp,
        options
    ]);
    const handleSelect = (e)=>{
        const selectedValue = e.target.value;
        if (selectedValue && (allowDuplicates || !values.includes(selectedValue))) {
            onChange([
                ...values,
                selectedValue
            ]);
        }
    };
    const handleRemove = (index)=>{
        onChange(values.filter((_, i)=>i !== index));
    };
    const handleRemoveMore = (e)=>{
        const indexToRemove = parseInt(e.target.value, 10);
        if (!isNaN(indexToRemove)) {
            onChange(values.filter((_, i)=>i !== indexToRemove));
        }
        // Reset select value to placeholder immediately after removal
        e.target.value = "placeholder";
    };
    const renderPills = ()=>{
        return visiblePills.map((value, index)=>/*#__PURE__*/ _jsxs("div", {
                className: "pill bg-white/10 text-white px-[5px] rounded-sm flex items-center gap-1 h-[22px] max-w-[120px]",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "truncate",
                        children: getLabel(value)
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "cursor-pointer z-10 flex-shrink-0 px-[3px]",
                        onClick: (e)=>{
                            e.stopPropagation();
                            handleRemove(index);
                        },
                        children: /*#__PURE__*/ _jsx(SIcon, {
                            type: "x",
                            size: 8
                        })
                    })
                ]
            }, `${value}-${index}`));
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: `relative group ${className}`,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                ref: containerRef,
                className: `cursor-pointer relative pr-3 
          min-h-[32px] bg-grayscale-dark border border-white/10 rounded-md 
           overflow-hidden grid grid-cols-[1fr,auto] gap-3 items-center justify-between text-[13px]`,
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: `flex flex-wrap gap-[5px] py-[3px] ${values.length > 0 ? "pl-[5px]" : "pl-3"}`,
                        children: [
                            renderPills(),
                            showMore && /*#__PURE__*/ _jsxs("div", {
                                className: "relative bg-white/10 text-white px-[5px] rounded-md flex items-center h-[22px] z-10",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        children: `+${values.length - visiblePills.length} more`
                                    }),
                                    /*#__PURE__*/ _jsxs("select", {
                                        onChange: handleRemoveMore,
                                        className: "cursor-pointer absolute top-0 left-0 right-0 bottom-0 opacity-0 bg-transparent !px-0 !mx-0 rounded-full !border-color-[rgba(0,0,0,0)] !border-0 !text-sm",
                                        value: "placeholder",
                                        children: [
                                            /*#__PURE__*/ _jsx("option", {
                                                value: "placeholder",
                                                disabled: true,
                                                children: "Remove option"
                                            }),
                                            values.slice(visiblePills.length).map((value, index)=>/*#__PURE__*/ _jsx("option", {
                                                    value: index + visiblePills.length,
                                                    children: getLabel(value)
                                                }, `${value}-${index + visiblePills.length}`))
                                        ]
                                    })
                                ]
                            }),
                            values.length === 0 && /*#__PURE__*/ _jsx("p", {
                                className: "text-[#6B7280] line-clamp-1 min-w-0 whitespace-break-spaces break-all flex-1 h-[18px]",
                                children: placeholder
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SIcon, {
                        className: `opacity-30 ${visiblePills.length > 0 ? "group-hover:opacity-100 group-hover:text-brand-primary transition" : ""}`,
                        type: visiblePills.length === 0 ? "chevron-down" : "plus-thick",
                        size: 10
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("select", {
                value: "",
                tabIndex: tabIndex,
                onChange: handleSelect,
                className: "cursor-pointer absolute top-0 left-0 right-0 bottom-0 opacity-0 bg-transparent !px-0 !mx-0 rounded-full !border-color-[rgba(0,0,0,0)] !border-0 !text-sm",
                children: [
                    /*#__PURE__*/ _jsx("option", {
                        value: "",
                        disabled: true,
                        children: placeholder
                    }),
                    availableOptions.map((option)=>/*#__PURE__*/ _jsx("option", {
                            value: option.value,
                            children: option.label
                        }, option.value))
                ]
            })
        ]
    });
};
